// buildDeployConfig — assemble the V3 deployment `config` (FRONTEND_DEPLOYMENT_V3.md).
// The deploy request is { sessionId, source, config } for Spring Boot / .NET, and
// { sessionId, source, projectConfig, deploymentConfig } for Mule. Code lives in a
// provider (github | azure-repos) that carries the per-service repos[]; the CI
// platform (github-actions | azure-devops) runs the pipeline. A GitHub codebase can
// still run on Azure Pipelines, so both blocks can appear together.
//
// repos: [{ service, repo, branch?, baseBranch? }] from RepoBranchRows. For a
// repository source deployed in place (run-branch), pass repos = [] (omitted).

export function buildV3Config({
  tech,
  ciPlatform,                 // "github-actions" | "azure-devops"
  codeProvider,               // "github" | "azure-repos"
  github,                     // { token, owner }
  azure,                      // { orgUrl, pat, project }
  repos = [],                 // per-service targets
  monoRepo,                   // { repo, branch?, baseBranch? } — one repository for the whole project
  githubValues,               // { variables?, secrets?, services? }
  azureValues,                // { variables?, secretVariables? }
  keyVault,                   // spring/dotnet: { enabled, name, subscriptionName, servicePrincipal:{...} }
  anypoint,                   // mule: { enabled, orgName, envName, credentials:{clientId,clientSecret}, nexusCredentials?:{username,password} }
} = {}) {
  const config = {};
  if (ciPlatform) config.ciPlatform = ciPlatform;

  const hasRepos = Array.isArray(repos) && repos.length > 0;
  const hasMono = monoRepo && monoRepo.repo;
  // One repository for the whole project → repo/branch/baseBranch instead of repos[].
  const applyMono = (block) => {
    block.repo = monoRepo.repo;
    if (monoRepo.branch) block.branch = monoRepo.branch;
    if (monoRepo.baseBranch) block.baseBranch = monoRepo.baseBranch;
  };

  // Code provider block (carries repos[] or a single monorepo).
  if (codeProvider === "github" && github) {
    config.github = { token: github.token || "", owner: github.owner || "" };
    if (hasMono) applyMono(config.github);
    else if (hasRepos) config.github.repos = repos;
    if (githubValues && githubValues.variables && Object.keys(githubValues.variables).length) config.github.variables = githubValues.variables;
    if (githubValues && githubValues.secrets && Object.keys(githubValues.secrets).length) config.github.secrets = githubValues.secrets;
    if (githubValues && githubValues.services && Object.keys(githubValues.services).length) config.github.services = githubValues.services;
  }
  if (codeProvider === "azure-repos" && azure) {
    config.azure = {
      credentials: { orgUrl: azure.orgUrl || "", pat: azure.pat || "" },
      project: azure.project || "",
    };
    if (hasMono) applyMono(config.azure);
    else if (hasRepos) config.azure.repos = repos;
    if (azureValues && azureValues.variables && Object.keys(azureValues.variables).length) config.azure.variables = azureValues.variables;
    if (azureValues && azureValues.secretVariables && Object.keys(azureValues.secretVariables).length) config.azure.secretVariables = azureValues.secretVariables;
  }

  // Azure Pipelines running a GitHub codebase still needs Azure creds + project.
  if (ciPlatform === "azure-devops" && codeProvider === "github" && azure) {
    config.azure = {
      ...(config.azure || {}),
      credentials: { orgUrl: azure.orgUrl || "", pat: azure.pat || "" },
      project: azure.project || "",
    };
    if (azureValues && azureValues.variables && Object.keys(azureValues.variables).length) config.azure.variables = azureValues.variables;
    if (azureValues && azureValues.secretVariables && Object.keys(azureValues.secretVariables).length) config.azure.secretVariables = azureValues.secretVariables;
  }

  // Key Vault (Spring Boot / .NET only).
  if ((tech === "springboot" || tech === "dotnet") && keyVault && keyVault.enabled) {
    config.keyVault = {
      enabled: true,
      name: keyVault.name || "",
      subscriptionName: keyVault.subscriptionName || "",
      servicePrincipal: {
        tenantId: keyVault.tenantId || "",
        subscriptionId: keyVault.subscriptionId || "",
        clientId: keyVault.clientId || "",
        clientSecret: keyVault.clientSecret || "",
      },
    };
  }

  // Anypoint (Mule only), incl. the EE repository (Nexus) login.
  if (tech === "mule" && anypoint && anypoint.enabled) {
    config.anypoint = {
      enabled: true,
      orgName: anypoint.orgName || "",
      envName: anypoint.envName || "",
      credentials: { clientId: anypoint.clientId || "", clientSecret: anypoint.clientSecret || "" },
    };
    if (anypoint.nexusUsername || anypoint.nexusPassword) {
      config.anypoint.nexusCredentials = { username: anypoint.nexusUsername || "", password: anypoint.nexusPassword || "" };
    }
  }

  return config;
}

// Endpoint path per technology (process backend, url.backendUrl). Mule uses the
// plantuml-deploy route in the V3 source flow.
export function deployEndpoint(tech) {
  if (tech === "springboot") return "/api/v1/springboot-deployment";
  if (tech === "dotnet") return "/api/v1/dotnet-deployment";
  return "/api/v1/plantuml-deploy"; // mule
}

// Mule carries the config under `deploymentConfig` + a `projectConfig`; the others
// use `config`. Returns the JSON body fields to merge alongside sessionId + source.
export function deployBodyFields(tech, config, version) {
  if (tech === "mule") return { projectConfig: { version: version || "1.0.0" }, deploymentConfig: config };
  return { config };
}
